import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Spark from "@/assets/spark/spark";
import palettes from "@/contexts/theme-context/config/palleteConfig";

const COLORS = [palettes.dark.colors.theme.brandColor3, "white", "white"];
const MAX_SPARKS = 5;
const SPAWN_INTERVAL = 800;
const SPARK_LIFETIME = 1500;

const SparksOverlay = ({ activeBandWhite = true }: { activeBandWhite?: boolean }) => {
  const [sparks, setSparks] = useState<{ id: number; x: number; y: number; size: number; color: string }[]>([]);

  const gradientStyle = useMemo(() => ({
    position: "absolute" as const,
    width: "150%",
    height: "100%",
    zIndex: 105,
    top: 0,
    mixBlendMode: "overlay" as const,
    background: activeBandWhite
      ? `linear-gradient(-120deg,
          transparent 30%,
          rgba(255, 255, 255, 0.9) 50%,
          transparent 70%)`
      : 'transparent',
    transform: "rotate(0deg)",
  }), [activeBandWhite]);

  const containerStyle = useMemo(() => ({
    position: "absolute" as const,
    height: "100%",
    width: "100%",
    zIndex: 100,
    top: 0,
    overflow: "hidden",
  }), []);

  const spawnSpark = useCallback(() => {
    if (sparks.length >= MAX_SPARKS) return;

    const x = Math.floor(Math.random() * 10) * 10;
    const y = Math.floor(Math.random() * 10) * 10;
    const newSpark = {
      id: Date.now(),
      x,
      y,
      size: Math.random() * 20 + 12,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
    };

    setSparks(prevSparks => [...prevSparks, newSpark]);

    setTimeout(() => {
      setSparks(prevSparks => prevSparks.filter(spark => spark.id !== newSpark.id));
    }, SPARK_LIFETIME);
  }, [sparks.length]);

  useEffect(() => {
    const interval = setInterval(spawnSpark, SPAWN_INTERVAL);
    return () => clearInterval(interval);
  }, [spawnSpark]);

  return (
    <>
      <motion.div
        style={gradientStyle}
        animate={{
          x: ["-100%", "100%"],
        }}
        transition={{
          duration: 4,
          ease: "linear",
          repeat: Infinity,
          repeatDelay: 3,
        }}
      />
      <motion.div style={containerStyle}>
        <AnimatePresence>
          {sparks.map((spark) => (
            <motion.div
              key={spark.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
              style={{
                position: "absolute",
                left: `${spark.x}%`,
                top: `${spark.y}%`,
                filter: `drop-shadow(0 0 1px ${spark.color})`,
              }}
            >
              <Spark color={spark.color} size={spark.size} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </>
  );
};

export default SparksOverlay;